import { InventoryItem, InventoryItemLifecycleStatus } from './inventory-item';
import { normalizeStatus } from './inventory-item-status';
import {
  InventoryItemRequest,
  InventoryPendingForm,
  InventorySubmissionDraftRequest,
} from './inventory-request';

export interface InventorySubmissionState {
  items: InventoryItem[];
  nextItemId: number;
  hasSubmitted: boolean;
  hasPendingChangesAfterSubmit: boolean;
  pendingForm?: InventoryPendingForm | null;
  autoSubmittedOn?: Date | null;
  autoSubmitReason?: string | null;
}

export function mapItemToRequest(item: InventoryItem): InventoryItemRequest {
  return {
    id: item.id,
    itemName: item.itemName,
    itemDescription: item.itemDescription,
    quantity: item.quantity,
    cost: item.cost,
    dateOfPurchase: new Date(item.dateOfPurchase).toISOString(),
    additionalItemDetails: item.additionalItemDetails,
    preLossCondition: item.preLossCondition,
    wherePurchased: item.wherePurchased,
    methodOfPayment: item.methodOfPayment,
    specifyMethodOfPayment: item.specifyMethodOfPayment,
    receiptOrInvoiceIncluded: item.receiptOrInvoiceIncluded,
    reasonForNoDocumentation: item.reasonForNoDocumentation,
    status: normalizeStatus(item.status),
    dateUploaded: new Date(item.dateUploaded).toISOString(),
  };
}

export function buildDraftRequest(
  state: InventorySubmissionState,
): InventorySubmissionDraftRequest {
  return {
    nextItemId: state.nextItemId,
    hasSubmitted: state.hasSubmitted,
    hasPendingChangesAfterSubmit: state.hasPendingChangesAfterSubmit,
    pendingForm: state.pendingForm ?? null,
    items: state.items.map(mapItemToRequest),
  };
}

export function mapDraftToState(
  draft: InventorySubmissionDraftRequest,
): InventorySubmissionState {
  return {
    nextItemId: draft.nextItemId ?? 1,
    hasSubmitted: draft.hasSubmitted ?? false,
    hasPendingChangesAfterSubmit: draft.hasPendingChangesAfterSubmit ?? false,
    autoSubmittedOn: draft.autoSubmittedOn
      ? new Date(draft.autoSubmittedOn)
      : null,
    autoSubmitReason: draft.autoSubmitReason ?? null,
    pendingForm: draft.pendingForm ?? null,
    items: (draft.items ?? []).map(mapRequestToItem),
  };
}

function mapRequestToItem(item: InventoryItemRequest): InventoryItem {
  return {
    id: item.id,
    itemName: item.itemName,
    itemDescription: item.itemDescription,
    quantity: item.quantity,
    cost: item.cost,
    dateOfPurchase: new Date(item.dateOfPurchase),
    additionalItemDetails: item.additionalItemDetails,
    preLossCondition: item.preLossCondition,
    wherePurchased: item.wherePurchased,
    methodOfPayment: item.methodOfPayment,
    specifyMethodOfPayment: item.specifyMethodOfPayment,
    receiptOrInvoiceIncluded: item.receiptOrInvoiceIncluded,
    reasonForNoDocumentation: item.reasonForNoDocumentation,
    status: normalizeStatus(item.status as InventoryItemLifecycleStatus),
    attachmentCount: item.attachmentCount ?? 0,
    dateUploaded: new Date(item.dateUploaded),
    attachments: (item.attachments ?? []).map((attachment) => ({
      attachmentId: attachment.attachmentId,
      inventoryDetailId: attachment.inventoryDetailId,
      fileName: attachment.fileName,
      dateUploaded: new Date(attachment.dateUploaded),
      status: (attachment.status as 'attached' | 'error') || 'attached',
      fileSize: attachment.fileSize,
      contentPurged: attachment.contentPurged,
    })),
  };
}
